import { ADD_COLOR_ID } from '@components/ArtBoard/ArtboardToolbar'
import { TagsContent } from '@components/ArtBoard/Node/components/TagsPopover'
import type { ID } from '@octopus-do/octopus-project-core'
import { Button, Loader } from '@octopus-do/octopus-storybook'
import { selectProjectParents } from '@store/reducers/dashboard/selectors'
import {
  StatusActions,
  type StatusOperation,
} from '@store/reducers/dashboard/statuses'
import type React from 'react'
import { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { OpenConfirmation } from '@/store'
import { StatusColors } from './StatusColors'
import css from './styles.module.css'

interface Props {
  projectId: ID
}
interface CustomColor {
  id: ID | null
  color: string
}

export const StatusContent: React.FC<Props> = ({ projectId }) => {
  const dispatch = useDispatch()
  const parents = useSelector(selectProjectParents(projectId), shallowEqual)
  const [newColor, setNewColor] = useState('#2F7FEF')
  const [custom, setCustom] = useState<CustomColor | null>(null)
  const workspace = parents?.workspace
  const statuses = workspace?.statuses ?? []
  const selected = parents?.project.status?.id ?? null
  const request = (operation: StatusOperation) =>
    dispatch(StatusActions.request({ projectId, operation }))

  const recolor = (id: ID | null, color: string) => {
    const status = statuses.find((item) => item.id === id)
    if (id === null) {
      setNewColor(color)
    } else if (status) {
      request({ type: 'update', id, changes: { color } })
    }
  }
  const chooseColor = (id: ID | null, keys: string[]) => {
    const color = keys[0]
    if (color === ADD_COLOR_ID) {
      setCustom({
        id,
        color: statuses.find((item) => item.id === id)?.color ?? newColor,
      })
    } else {
      recolor(id, color)
    }
  }
  const remove = (id: ID) => {
    const title = statuses.find((item) => item.id === id)?.title ?? ''
    dispatch(
      OpenConfirmation({
        title: 'Delete status',
        label: 'Delete',
        message: `Delete “${title}” from this workspace? Projects using it will have no status.`,
        then: [
          StatusActions.request({
            projectId,
            operation: { type: 'delete', id },
          }),
        ],
      })
    )
  }

  if (!workspace?.statuses) {
    if (workspace?.statusesError) {
      return <Button onClick={() => request({ type: 'load' })}>Retry</Button>
    }
    return <Loader />
  }

  return (
    <div className={css.content}>
      <TagsContent
        colorHex={custom?.color ?? newColor}
        picker={{
          area: null,
          shown: custom !== null,
          onPreview: () => {},
          onClose: () => setCustom(null),
          onAddColor: (color) => {
            if (custom) {
              recolor(custom.id, color)
            }
            setCustom(null)
          },
        }}
        colorMenu={StatusColors}
        newTagColor={{ color: newColor, colorId: newColor }}
        tags={statuses.map((status) => ({
          ...status,
          checked: status.id === selected,
          colorId: status.color,
        }))}
        onToggle={(id) => {
          const statusId = selected === id ? null : id
          request({ type: 'set', statusId })
        }}
        onCreate={(title) =>
          request({ type: 'create', title, color: newColor })
        }
        onRename={(id, title) => {
          const status = statuses.find((item) => item.id === id)
          if (status) {
            request({ type: 'update', id, changes: { title } })
          }
        }}
        onDelete={remove}
        onMove={(id, direction) => request({ type: 'move', id, direction })}
        onColorMenu={chooseColor}
        onNewTagColorMenu={(keys) => chooseColor(null, keys)}
      />
    </div>
  )
}
